import { CheckCircle2, Circle, ChevronRight, Sparkles, PartyPopper, X, SkipForward, Undo2, Share2, Copy, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { useConfigurationProgress } from '@/hooks/useConfigurationProgress';
import { useWorkspaceContext } from '@/contexts/WorkspaceContext';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';


export function ConfigurationProgress() {
  const { 
    steps, 
    completedCount, 
    totalCount, 
    percentage, 
    isLoading, 
    justCompleted, 
    isHidden,
    dismissCelebration,
    skipStep,
    unskipStep,
    hideForever,
  } = useConfigurationProgress();

  // Get workspace for public URL - MUST be called before any early returns
  const { activeWorkspace } = useWorkspaceContext();
  const publicPageUrl = `${window.location.origin}/espace/${activeWorkspace?.slug}`;

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicPageUrl);
      toast.success('URL copiée dans le presse-papier');
    } catch {
      toast.error('Impossible de copier l\'URL');
    }
  };

  // Don't render anything if hidden (avoids flash)
  if (isHidden) {
    return null;
  }

  // Only show skeleton during initial load if not hidden
  if (isLoading) {
    return (
      <div className="bg-card rounded-lg border border-border shadow-sm p-6 animate-pulse">
        <div className="h-5 w-48 bg-muted rounded mb-4" />
        <div className="h-4 w-full bg-muted rounded mb-4" />
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-8 bg-muted rounded" />
          ))}
        </div>
      </div>
    );
  }

  // Show celebration card if just completed
  if (percentage === 100 && justCompleted) {
    return (
      <div
        className="bg-gradient-to-br from-luminjo-success/10 via-primary/10 to-luminjo-warning/10 rounded-lg border border-luminjo-success/30 shadow-sm p-6 relative animate-in fade-in-0 zoom-in-95 duration-200"
      >
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 h-8 w-8 text-muted-foreground hover:text-foreground"
          onClick={dismissCelebration}
          aria-label="Fermer"
        >
          <X className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-full bg-luminjo-success/20">
            <PartyPopper className="h-8 w-8 text-luminjo-success" />
          </div>
          <div>
            <h3 className="font-semibold text-lg text-foreground">Félicitations ! 🎉</h3>
            <p className="text-sm text-muted-foreground">
              Votre compte est entièrement configuré. Vous êtes prêt à offrir un support exceptionnel !
            </p>
          </div>
        </div>

        {/* Séparateur */}
        <div className="border-t border-luminjo-success/20 my-4" />

        {/* Message de partage */}
        <div className="flex items-start gap-3">
          <Share2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <p className="text-sm text-foreground">
              N'oubliez pas de partager l'adresse de votre page pour que vos clients puissent vous contacter :
            </p>
            <div className="flex items-center gap-2 p-2 bg-background/50 rounded-md border border-border">
              <code className="text-xs flex-1 truncate text-muted-foreground">{publicPageUrl}</code>
              <Button 
                size="sm" 
                variant="ghost" 
                className="h-7 w-7 p-0 flex-shrink-0"
                onClick={copyUrl}
                aria-label="Copier l'URL"
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <a
              href={`/espace/${activeWorkspace?.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
            >
              Ouvrir la page
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Don't show if fully completed (and not just completed)
  if (percentage === 100) {
    return null;
  }

  const nextStep = steps.find(s => !s.completed && !s.skipped);

  return (
    <div
      className="bg-card rounded-lg border border-border shadow-sm p-6 animate-in fade-in-0 slide-in-from-bottom-5 duration-300"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-primary/10">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Configuration du compte</h3>
            <p className="text-sm text-muted-foreground">
              {completedCount} sur {totalCount} étapes complétées
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold text-primary">{percentage}%</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
            onClick={hideForever}
            title="Masquer définitivement"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Progress value={percentage} className="h-2 mb-4" />

      <div className="space-y-2">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className="group animate-in fade-in-0 slide-in-from-left-2 duration-200"
                style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'backwards' }}
              >
                <div
                  className={cn(
                    "flex items-center justify-between p-2 rounded-lg transition-colors",
                    step.completed 
                      ? "bg-luminjo-success/5" 
                      : step.skipped
                        ? "bg-muted/30"
                        : "hover:bg-muted/50"
                  )}
                >
                  <Link
                    to={step.link}
                    className="flex items-center gap-3 flex-1 min-w-0"
                  >
                    {step.completed ? (
                      <CheckCircle2 className="h-5 w-5 text-luminjo-success flex-shrink-0" />
                    ) : step.skipped ? (
                      <SkipForward className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    ) : (
                      <Circle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    )}
                    <div className="min-w-0">
                      <span className={cn(
                        "text-sm block",
                        step.completed 
                          ? "text-muted-foreground line-through" 
                          : step.skipped
                            ? "text-muted-foreground italic"
                            : "text-foreground"
                      )}>
                        {step.label}
                        {step.skipped && <span className="ml-2 text-xs">(ignoré)</span>}
                      </span>
                      {!step.completed && !step.skipped && (
                        <span className="text-xs text-muted-foreground block truncate">
                          {step.description}
                        </span>
                      )}
                    </div>
                  </Link>
                  
                  <div className="flex items-center gap-2">
                    {!step.completed && !step.skipped && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 opacity-0 group-hover:opacity-100 transition-opacity text-xs text-muted-foreground hover:text-foreground"
                        onClick={(e) => {
                          e.preventDefault();
                          skipStep(step.id);
                        }}
                      >
                        Ignorer
                      </Button>
                    )}
                    
                    {step.skipped && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                        onClick={(e) => {
                          e.preventDefault();
                          unskipStep(step.id);
                        }}
                      >
                        <Undo2 className="h-3 w-3 mr-1" />
                        Restaurer
                      </Button>
                    )}
                    
                    {!step.completed && !step.skipped && (
                      <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                  </div>
                </div>
              </div>
          ))}
      </div>

      {nextStep && (
        <div className="mt-4 pt-4 border-t border-border">
          <Link 
            to={nextStep.link}
            className="flex items-center gap-2 text-sm text-primary hover:underline"
          >
            <span>Prochaine étape : {nextStep.label}</span>
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      )}
    </div>
  );
}
